const FO_UPSELL_CART_URL = '/cart.js';
const FO_UPSELL_ADD_URL = '/cart/add.js';
const FO_UPSELL_SOURCE = 'cart-drawer-fo-upsell';

let foUpsellLastCart = null;
let foUpsellCartMutated = false;

/**
 * Whether a cartUpdate payload is a whole cart rather than a /cart/add.js partial.
 *
 * /cart/add.js answers with "the line items associated with the added items" — an
 * `{ items: [...] }` object describing only what was just added, with none of the
 * cart-level fields. /cart.js, /cart/change.js and /cart/update.js all answer with the
 * full cart document, and every documented example of that document carries `token`.
 * Several publishers hand their raw add.js response straight to cartUpdate
 * (product-form.js:154, section-product-main.js:620), so partials really do arrive here.
 *
 * The previous test — item_count is a number, items is an array — happened to reject
 * today's partial, but only because `item_count` is one of the fields add.js omits. That
 * is a coincidence of which fields the endpoint returns, not a statement about
 * completeness, and it is one field away from silently accepting a partial. `token` is a
 * deliberate discriminator instead of an accidental one: it appears in every documented
 * cart document and in none of the documented add.js responses. Shopify does not formally
 * promise its absence from a partial, so treat this as strictly narrowing the old test —
 * a false negative just costs one extra /cart.js read, which is the old behaviour anyway.
 *
 * This matters here because the upsell decides on the cart's *contents* — which primary
 * line is present, and whether the upsell variant is already in the cart. Deciding that
 * from a partial means reading a two-line cart as if it held only the line just added, and
 * offering an upsell that is already in the basket.
 */
function isFoUpsellCompleteCart(cart) {
  return !!cart && typeof cart.token === 'string' && typeof cart.item_count === 'number' && Array.isArray(cart.items);
}

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
    // Any cart change retires the page-load snapshot, even one announced as a partial we
    // cannot cache: /cart/add.js answers with { items } alone, so a partial leaves nothing
    // behind, and reading the snapshot afterwards would decide against a pre-add cart.
    foUpsellCartMutated = true;

    if (isFoUpsellCompleteCart(data?.cartData)) {
      foUpsellLastCart = data.cartData;
    }
  });
}

if (!customElements.get('fo-upsell-card')) {
  class FoUpsellCard extends HTMLElement {
    constructor() {
      super();
      this.matrix = {};
      this.cards = [];
      this.pillTemplate = null;
      this.pills = [];
      this.pillTargets = new Map();
      this.activeCard = null;
      this.activeNode = null;
      this.activeTargets = [];
      this.activeTarget = null;
      this.activePurchaseType = 'subscription';
      this.cartUpdateUnsubscriber = null;
      this.pendingInitialResolve = false;
      this.boundOnAddClick = this.onAddClick.bind(this);
      this.boundOnPillsClick = this.onPillsClick.bind(this);
      this.boundOnPillsKeydown = this.onPillsKeydown.bind(this);
    }

    connectedCallback() {
      this.matrix = this.parseMatrix();
      this.cards = Array.from(this.querySelectorAll('[data-fo-upsell-product]'));
      this.pillTemplate = this.querySelector('[data-fo-upsell-pill-tpl]');
      if (!this.cards.length || !this.pillTemplate) {
        this.hide();
        return;
      }

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));

      this.pendingInitialResolve = true;
      queueMicrotask(() => {
        if (!this.pendingInitialResolve || !this.isConnected) return;
        this.pendingInitialResolve = false;
        // resolveFromCart now prefers the cached snapshot itself, so both the remount case
        // and a genuine cold start go through the one path.
        this.resolveFromCart();
      });
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      this.cartUpdateUnsubscriber = null;
      this.teardownActiveCardListeners();
    }

    parseMatrix() {
      const raw = window.foUpsellMatrix;
      if (typeof raw !== 'string' || !raw.trim()) return {};
      try {
        return JSON.parse(raw) || {};
      } catch (error) {
        return {};
      }
    }

    onCartUpdate(data) {
      if (data?.source === FO_UPSELL_SOURCE) return;

      this.pendingInitialResolve = false;

      const cart = data?.cartData;
      if (isFoUpsellCompleteCart(cart)) {
        this.resolve(cart);
      } else {
        this.resolveFromCart();
      }
    }

    /**
     * Resolves against the freshest complete cart, reading over the network only when there
     * isn't one to hand.
     *
     * The cache is what makes a remount free. This element lives inside .drawer__inner
     * (snippets/cart-drawer.liquid), which cart-drawer.js renderContents replaces wholesale
     * on every cart change — so the element is destroyed and a new one connects on every
     * add-to-cart, and its connectedCallback used to buy a fresh /cart.js each time. That
     * read cost 332 ms on the critical path of every add (IDS-7204) to fetch a cart the
     * publisher had just broadcast: the mutation that triggered the re-render published its
     * own complete cart, which foUpsellLastCart captured before the new element existed.
     *
     * On a genuine cold start the page-load snapshot shared with the free-gift engine
     * (window.getCartSnapshot, assets/cart-request.js) answers instead of a second /cart.js.
     * "Cold start" has to be tracked explicitly via foUpsellCartMutated rather than inferred
     * from a null cache: /cart/add.js answers with { items } alone, so an add publishes a
     * partial that never reaches the cache, and a null cache therefore does NOT imply an
     * untouched cart. Reading the frozen snapshot in that state resolved the upsell against
     * the pre-add cart and hid the card on every add-to-cart.
     *
     * The live network read stays as the last resort — no snapshot helper on the page, or a
     * failed snapshot — so the card still appears in exactly the conditions it did before.
     */
    resolveFromCart() {
      if (isFoUpsellCompleteCart(foUpsellLastCart)) {
        this.resolve(foUpsellLastCart);
        return;
      }

      if (!foUpsellCartMutated && typeof window.getCartSnapshot === 'function') {
        window.getCartSnapshot().then((cart) => {
          if (isFoUpsellCompleteCart(foUpsellLastCart)) {
            // A mutation landed while the snapshot was in flight — it outranks the snapshot.
            this.resolve(foUpsellLastCart);
          } else if (foUpsellCartMutated) {
            // Mutated, but the publisher only sent a partial, so there is no cart to reuse.
            this.fetchCartAndResolve();
          } else if (isFoUpsellCompleteCart(cart)) {
            this.resolve(cart);
          } else {
            this.fetchCartAndResolve();
          }
        });
        return;
      }

      this.fetchCartAndResolve();
    }

    fetchCartAndResolve() {
      window
        .cartFetchWithRetry(FO_UPSELL_CART_URL)
        .then((response) => response.json())
        .then((cart) => this.resolve(cart))
        .catch(() => this.hide());
    }

    resolve(cart) {
      this.hidden = true;

      if (!cart || !Array.isArray(cart.items) || !Object.keys(this.matrix).length) {
        this.hide();
        return;
      }

      const primary = this.findPrimaryLine(cart.items);
      if (!primary) {
        this.hide();
        return;
      }

      const node = this.matrix[primary.productType];
      const weight = this.normalizeWeight(primary.rawWeight);
      const targets = node?.weights?.[weight];

      if (!node || !Array.isArray(targets) || !targets.length) {
        this.hide();
        return;
      }

      if (this.upsellAlreadyInCart(cart.items, node, targets)) {
        this.hide();
        return;
      }

      this.activePurchaseType = primary.item.selling_plan_allocation ? 'subscription' : 'otp';
      this.activeNode = node;
      this.activeTargets = targets;

      this.revealCard(targets[0]);
    }

    // Entries may override the node-level product (upsell handle + variant ids), e.g. FO 8oz vs 16oz.
    effectiveHandle(target) {
      return target?.upsell || this.activeNode?.upsell || '';
    }

    effectiveVariantId(target) {
      return target?.variant || this.activeNode?.variant || '';
    }

    effectiveOtpVariantId(target) {
      return target?.otp_variant || this.activeNode?.otp_variant || '';
    }

    upsellAlreadyInCart(items, node, targets) {
      const ids = [node.variant, node.otp_variant];
      targets.forEach((target) => ids.push(target.variant, target.otp_variant));
      const uniqueIds = ids.filter(Boolean).map((id) => String(id));
      if (!uniqueIds.length) return false;
      return items.some((item) => uniqueIds.includes(String(item.variant_id || item.id)));
    }

    findPrimaryLine(items) {
      for (const item of items) {
        const productType = item?.properties?._product_type;
        if (!productType || !this.matrix[productType]) continue;
        const rawWeight = item?.properties?._jc_dogsize || '';
        return { item, productType, rawWeight };
      }
      return null;
    }

    normalizeWeight(raw) {
      return String(raw || '')
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/lbs/g, '');
    }

    revealCard(activeTarget) {
      this.teardownActiveCardListeners();

      const handle = this.effectiveHandle(activeTarget);
      let matched = null;
      this.cards.forEach((card) => {
        if (card.dataset.foUpsellProduct === handle) {
          matched = card;
          card.removeAttribute('hidden');
        } else {
          card.setAttribute('hidden', '');
        }
      });

      if (!matched) {
        this.hide();
        return;
      }

      this.activeCard = matched;
      this.addButton = matched.querySelector('[data-fo-upsell-add]');
      this.pillsContainer = matched.querySelector('[data-fo-upsell-pills]');
      this.priceBlock = matched.querySelector('.cart-drawer-fo-upsell__card-price');
      this.priceOriginal = matched.querySelector('[data-fo-upsell-price-original]');
      this.priceFinal = matched.querySelector('[data-fo-upsell-price-final]');
      this.unitLabel = matched.querySelector('[data-fo-upsell-unit]');
      // Announce price changes (pill switch / purchase-type) to screen readers.
      this.priceBlock?.setAttribute('aria-live', 'polite');

      this.buildPills(this.activeTargets);
      const activePill =
        this.pills.find((pill) => this.pillTargets.get(pill) === activeTarget) || this.pills[0] || null;
      this.applyActiveTarget(activeTarget, activePill);

      this.addButton?.addEventListener('click', this.boundOnAddClick);
      this.pillsContainer?.addEventListener('click', this.boundOnPillsClick);
      this.pillsContainer?.addEventListener('keydown', this.boundOnPillsKeydown);

      this.show();
    }

    buildPills(targets) {
      this.pills = [];
      this.pillTargets = new Map();
      if (!this.pillsContainer) return;

      this.pillsContainer.innerHTML = '';

      // 1 target → no choice → leave the row empty (CSS hides an empty row).
      if (targets.length <= 1) {
        this.pillsContainer.setAttribute('hidden', '');
        return;
      }

      this.pillsContainer.removeAttribute('hidden');
      this.pillsContainer.setAttribute('role', 'radiogroup');

      targets.forEach((target, index) => {
        const fragment = this.pillTemplate.content.cloneNode(true);
        const pill = fragment.querySelector('[data-fo-pill]');
        if (!pill) return;

        pill.dataset.sellingPlan = target.plan || '';
        pill.dataset.qty = target.qty || 1;
        pill.dataset.label = target.label || '';

        const labelSpan = pill.querySelector('[data-fo-pill-label]');
        if (labelSpan) labelSpan.textContent = target.label || '';

        pill.setAttribute('role', 'radio');
        pill.setAttribute('aria-checked', 'false');
        pill.tabIndex = index === 0 ? 0 : -1;

        // Target object lives in a Map keyed by the pill element — never on the DOM node.
        this.pillTargets.set(pill, target);

        this.pillsContainer.appendChild(pill);
        this.pills.push(pill);
      });
    }

    onPillsClick(event) {
      const pill = event.target.closest('[data-fo-pill]');
      if (!pill || !this.pills.includes(pill)) return;
      this.selectPill(pill);
    }

    onPillsKeydown(event) {
      if (!this.pills.length) return;

      const currentIndex = this.pills.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex = null;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % this.pills.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + this.pills.length) % this.pills.length;
          break;
        case ' ':
        case 'Enter':
          event.preventDefault();
          this.selectPill(this.pills[currentIndex]);
          return;
        default:
          return;
      }

      event.preventDefault();
      const nextPill = this.pills[nextIndex];
      nextPill.focus();
      this.selectPill(nextPill);
    }

    selectPill(pill) {
      const target = this.pillTargets.get(pill);
      const hadFocus = document.activeElement === pill;
      const priorCard = this.activeCard;
      this.setActiveTarget(target, pill);
      // A card switch rebuilds the pills — restore focus onto the freshly built active pill.
      if (hadFocus && this.activeCard !== priorCard) {
        const activePill = this.pills.find((candidate) => this.pillTargets.get(candidate) === target);
        activePill?.focus();
      }
    }

    setActiveTarget(target, activePill) {
      if (!target) return;

      // Target may live on another product card (per-entry upsell override) — remount there.
      if (this.activeCard?.dataset.foUpsellProduct !== this.effectiveHandle(target)) {
        this.revealCard(target);
        return;
      }

      this.applyActiveTarget(target, activePill);
    }

    applyActiveTarget(target, activePill) {
      if (!target) return;
      this.activeTarget = target;

      this.pills?.forEach((pill) => {
        const isActive = pill === activePill;
        pill.classList.toggle('cart-drawer-fo-upsell__pill--selected', isActive);
        pill.setAttribute('aria-checked', isActive ? 'true' : 'false');
        pill.tabIndex = isActive ? 0 : -1;
      });

      this.updateAddButton();
      this.updatePrice();
      this.updateUnitLabel();
    }

    updateUnitLabel() {
      if (!this.unitLabel || !this.activeTarget) return;
      const qty = parseInt(this.activeTarget.qty, 10) || 1;
      const singular = this.unitLabel.dataset.unitOne || '';
      const plural = this.unitLabel.dataset.unitTwo || '';
      if (qty === 1) {
        this.unitLabel.textContent = singular || `${qty}`;
        return;
      }
      // qty 2 uses the plural form as-is; any other N swaps the plural's number for N.
      this.unitLabel.textContent = plural ? plural.replace(/\d+/, String(qty)) : `${qty}`;
    }

    updateAddButton() {
      if (!this.addButton || !this.activeTarget) return;

      // Variant ids resolve per target (entry override → node fallback); the
      // selling plan + qty come from the active target.
      this.addButton.dataset.variantId = this.effectiveVariantId(this.activeTarget);
      this.addButton.dataset.otpVariantId = this.effectiveOtpVariantId(this.activeTarget);
      this.addButton.dataset.sellingPlan = this.activeTarget.plan || '';
      this.addButton.dataset.qty = this.activeTarget.qty || 1;
    }

    updatePrice() {
      if (!this.priceFinal) return;

      const subFinal = parseInt(this.priceBlock?.dataset.priceSubFinal, 10);
      const subOriginal = parseInt(this.priceBlock?.dataset.priceSubOriginal, 10);
      const otp = parseInt(this.priceBlock?.dataset.priceOtp, 10);

      if (this.activePurchaseType === 'subscription') {
        // Show struck full price + discounted final.
        if (this.priceOriginal) {
          this.priceOriginal.hidden = false;
          this.priceOriginal.textContent = this.formatMoney(subOriginal);
        }
        this.priceFinal.textContent = this.formatMoney(subFinal);
      } else {
        // OTP — no automatic discount applies → full price only, no strike.
        if (this.priceOriginal) this.priceOriginal.hidden = true;
        this.priceFinal.textContent = this.formatMoney(otp);
      }
    }

    formatMoney(cents) {
      const parsed = parseInt(cents, 10);
      if (Number.isNaN(parsed)) return '$0.00';
      return `$${(parsed / 100).toFixed(2)}`;
    }

    onAddClick(event) {
      event.preventDefault();
      if (!this.addButton || this.addButton.disabled || this.addButton.classList.contains('loading')) {
        return;
      }
      this.addToCart();
    }

    async addToCart() {
      const qty = parseInt(this.addButton.dataset.qty, 10) || 1;
      const isSubscription = this.activePurchaseType === 'subscription';

      const variantId = isSubscription ? this.addButton.dataset.variantId : this.addButton.dataset.otpVariantId;
      const sellingPlan = isSubscription ? this.addButton.dataset.sellingPlan : '';

      if (!variantId) return;

      this.setLoadingState(true);

      try {
        const data = await this.postAdd(variantId, qty, sellingPlan);

        if (!data || data.status === 422 || data.status === 'error') {
          return;
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.renderContents && data.sections) {
          cartDrawer.renderContents(data, { open: false });
        }

        const fullCart = await fetch(FO_UPSELL_CART_URL)
          .then((r) => r.json())
          .catch(() => null);

        if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: FO_UPSELL_SOURCE,
            productVariantId: variantId,
            cartData: fullCart || undefined,
          });
        }
      } catch (error) {
        // Non-blocking — an upsell failure must not break the cart flow.
      } finally {
        this.setLoadingState(false);
      }
    }

    postAdd(variantId, quantity, sellingPlan) {
      const payload = {
        id: variantId,
        quantity,
      };

      if (sellingPlan && sellingPlan.trim() !== '') {
        payload.selling_plan = sellingPlan;
      }

      const sections = this.sectionIds();
      if (sections) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      return window
        .cartFetchWithRetry(FO_UPSELL_ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        })
        .then((response) => {
          /*
           * A 429 body is Shopify's HTML throttle page, not JSON — calling .json() on it throws
           * a SyntaxError that reads as a code bug rather than a rejected request. Reject on any
           * non-ok status instead so the caller's catch handles the throttle case explicitly.
           * 422 ("sold out") still arrives as an ok:false-free JSON error and keeps its old path.
           */
          if (!response.ok && response.status !== 422) {
            return Promise.reject(new Error(`Cart add failed with status ${response.status}`));
          }
          return response.json();
        });
    }

    sectionIds() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.getSectionsToRender) {
        return cartDrawer
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
      }
      return null;
    }

    setLoadingState(isLoading) {
      if (!this.addButton) return;
      const icon = this.addButton.querySelector('.cart-drawer-fo-upsell__add-icon');
      const spinner = this.addButton.querySelector('.loading__spinner');

      this.addButton.classList.toggle('loading', isLoading);
      this.addButton.disabled = isLoading;
      if (icon) icon.classList.toggle('hidden', isLoading);
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    show() {
      this.hidden = false;
    }

    hide() {
      this.hidden = true;
      this.cards?.forEach((card) => card.setAttribute('hidden', ''));
    }

    teardownActiveCardListeners() {
      this.addButton?.removeEventListener('click', this.boundOnAddClick);
      this.pillsContainer?.removeEventListener('click', this.boundOnPillsClick);
      this.pillsContainer?.removeEventListener('keydown', this.boundOnPillsKeydown);
    }
  }

  customElements.define('fo-upsell-card', FoUpsellCard);
}
